import { KeyRound, LogOut, Menu } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { NotificationTray } from '@/components/NotificationTray';
import { Button } from '@/components/ui/button';
import { signOut, useSession } from '@/lib/auth-client';
import { ROLE_LABELS } from '@shared/schemas/user';

/**
 * Notifications and the avatar menu.
 *
 * **The global search box has been removed, and that is a decision rather than
 * a deletion.** It sat here disabled from slice 2.4, reading "Search — arrives
 * in phase 10", and **no slice of phase 10 ever built it** — the roadmap's §10
 * is expenses, timesheets and reports. Closing phase 10 would therefore have
 * turned a promise into a false one on the screen the office opens most, which
 * is exactly the failure `adminRoutes.ts` exists to prevent for pages and had no
 * equivalent for controls.
 *
 * Asked and answered by the owner on 2026-08-06: a search across everything is
 * not wanted; a search where a list gets long is. **That already exists** —
 * `/admin/clients` searches name, business name, email and phone, and `/admin/jobs`,
 * `/admin/employees` and `/admin/leads` all take a search term too. So this box
 * was offering, badly, something the app already does well one screen in.
 *
 * Removed rather than relabelled: a permanently disabled control is furniture,
 * and "explain, don't hide" (design-system.md §7) is about a feature that is
 * coming, not one nobody scheduled.
 */
export function Topbar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;

    function onPointerDown(event: MouseEvent): void {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    // Escape closes it too — a menu you can only leave with the mouse is not
    // keyboard reachable, and design-system.md §9.4 asks for a keyboard path.
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const user = session?.user;
  const initial = user?.name.trim().charAt(0).toUpperCase() ?? '?';

  async function onSignOut(): Promise<void> {
    await signOut();
    await navigate('/login', { replace: true });
  }

  return (
    <header className="sticky top-0 z-20 flex items-center gap-2 border-b border-ink-300 bg-white px-3 py-2 sm:px-4">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        aria-label="Open menu"
        onClick={onOpenMenu}
      >
        <Menu aria-hidden="true" />
      </Button>

      {/* Where the search box was. The menu button and the avatar hold the two
          ends of the bar, so this is what keeps them apart. */}
      <div className="min-w-0 flex-1" />

      {/*
        **`ml-auto` is what puts this group against the right edge**, and its
        absence is why the account block sat near the middle of a wide screen.
        The search field is `flex-1` but capped at `max-w-md`, so past about
        900px it stops growing and the free space has nowhere to go — the bell
        and the avatar simply carried on sitting next to it. An auto margin
        absorbs that space instead.

        A wrapper rather than `ml-auto` on the bell itself, so that anything
        added between the search box and these two does not silently become the
        thing that is right-aligned.
      */}
      <div className="ml-auto flex items-center gap-2">
        <NotificationTray basePath="/api/admin" />

        <div ref={menuRef} className="relative">
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((open) => !open);
            }}
            className="flex min-h-touch min-w-touch items-center gap-2 rounded-md px-1.5 hover:bg-honey-50"
          >
            {/*
            ink-900 on honey-400 at 14.75:1. The prototype's avatar initial is
            honey on honey — 1.0:1, so the letter is simply not there (P27).
          */}
            <span
              aria-hidden="true"
              className="grid size-9 place-items-center rounded-full bg-honey-400 text-label font-semibold text-ink-900"
            >
              {initial}
            </span>
            <span className="sr-only">Account menu</span>
            <span className="hidden text-left sm:block">
              <span className="block text-label leading-tight text-ink-900">{user?.name}</span>
              <span className="block text-meta leading-tight text-ink-500">
                {user ? ROLE_LABELS[user.role] : ''}
              </span>
            </span>
          </button>

          {menuOpen ? (
            <div
              role="menu"
              className="absolute right-0 top-full z-30 mt-1 w-56 rounded-md border border-ink-300 bg-white p-1 shadow-pop"
            >
              <div className="border-b border-ink-300 px-3 py-2">
                <p className="truncate text-label text-ink-900">{user?.name}</p>
                {/*
                Honest about an absent value rather than an em-dash — the client
                portal's pattern, which design-system.md §7 says wins. A username
                account has no email and that is fine; saying so beats a blank.
              */}
                <p className="truncate text-meta text-ink-500">
                  {user?.email ?? <span className="italic">Signs in with a username</span>}
                </p>
              </div>

              <Link
                to="/change-password"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                }}
                className="flex min-h-touch items-center gap-2.5 rounded-sm px-3 text-body text-ink-700 hover:bg-honey-50"
              >
                <KeyRound aria-hidden="true" className="size-[18px]" />
                Change password
              </Link>

              <button
                type="button"
                role="menuitem"
                onClick={() => void onSignOut()}
                className="flex min-h-touch w-full items-center gap-2.5 rounded-sm px-3 text-body text-ink-700 hover:bg-honey-50"
              >
                <LogOut aria-hidden="true" className="size-[18px]" />
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
